/* eslint-disable no-console */
import { useCart } from '@lib/hooks/use-cart'
import { useMe } from '@lib/hooks/use-me'
import { useSetEmarsysStore } from '@lib/use-emarsys-store'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { typedWindow } from './types'

const isDev = ['staging', 'preview', 'development'].includes(
  process.env.NEXT_PUBLIC_STAGE as string,
)

type QueueItem = [string, any]

type Queue = QueueItem[]

type CartItems = {
  item: string
  price: number
  quantity: number
}[]

export function useEmarsysTracking() {
  const { cart, fetching: isCartFetching } = useCart()
  const { me, isLoading: isAuthLoading } = useMe()
  const customerId = me?.dafUser?.klant_nr
  const queue: Queue = useMemo(() => [], [])
  const [isTrackingReady, setIsTrackingReady] = useState(false)
  const { setProductIds } = useSetEmarsysStore()

  useEffect(() => {
    if (!isAuthLoading && !isCartFetching && cart !== null) {
      setIsTrackingReady(true)
    }
  }, [isAuthLoading, isCartFetching, cart])

  const devLog = useCallback(
    (functionName: string, data?: any) => {
      if (isDev) {
        const logData = {
          functionName,
          queueState: JSON.parse(JSON.stringify(queue)),
          eventData: data ?? null,
        }
        console.groupCollapsed(
          `%c[EmarsysTracking] ${functionName}`,
          'color: #b39ddb; font-weight: bold;',
        )
        console.info('ScarabQueue state:', logData.queueState)
        console.groupEnd()
      }
    },
    [queue],
  )

  const ensureQueue = useCallback((origin: string) => {
    if (!typedWindow?.ScarabQueue) {
      console.error(`[EmarsysTracking][${origin}] ScarabQueue is not defined`)
      throw new Error(`ScarabQueue is not defined for ${origin}`)
    } else if (isDev) {
      typedWindow.ScarabQueue.push(['testMode'])
    }
  }, [])

  const pushToQueue = useCallback(
    (item: QueueItem) => {
      typedWindow.ScarabQueue.push(item)
      queue.push(item)
    },
    [queue],
  )

  const setDefaultSettings = useCallback(() => {
    ensureQueue('setDefaultSettings')

    if (customerId) {
      pushToQueue(['setCustomerId', customerId.toString()])
    }

    const items: CartItems =
      cart?.items?.map((item) => ({
        item: item.articleId,
        price: item.totalPrice / 100,
        quantity: item.quantity,
      })) ?? []

    pushToQueue(['cart', items])
    devLog('setDefaultSettings', { customerId, cartItems: cart?.items.length })
  }, [customerId, cart, devLog, ensureQueue, pushToQueue])

  const setCategoryVisit = (category: string) => {
    ensureQueue('setCategoryVisit')

    pushToQueue(['category', category])
    devLog('setCategoryVisit', { category })
  }

  const setHomepageVisit = () => {
    ensureQueue('setHomepageVisit')

    pushToQueue([
      'recommend',
      {
        logic: 'HOME',
        success: (SC: any) => handleSuccess(SC, 'featured'),
        containerId: 'featured-products-home',
      },
    ])
    devLog('setHomepageVisit')
  }

  const setCustomPageVisit = (pageName: string) => {
    ensureQueue('setCustomPageVisit')

    pushToQueue(['view', pageName])

    devLog('setCustomPageVisit', { pageName })
  }

  const setProductVisit = (productId: string) => {
    ensureQueue('setProductVisit')

    pushToQueue(['view', productId])
    pushToQueue([
      'recommend',
      {
        logic: 'RELATED',
        success: (SC: any) => handleSuccess(SC, 'related'),
        containerId: 'related-products',
      },
    ])
    devLog('setProductVisit', { productId })
  }

  const setPurchase = (
    orderId: string,
    items: { item: string; price: number; quantity: number }[],
  ) => {
    ensureQueue('setPurchase')

    pushToQueue(['purchase', { orderId, items }])
    devLog('setPurchase', { orderId, items })
  }

  const setShoppingCartVisit = () => {
    ensureQueue('setShoppingCartVisit')

    pushToQueue([
      'recommend',
      {
        logic: 'CART',
        containerId: 'recommended-products-cart',
        limit: '8',
        success: (SC: any) => handleSuccess(SC, 'recommended'),
      },
    ])
    devLog('setShoppingCartVisit')
  }

  const handleSuccess = (SC: any, key: 'related' | 'featured' | 'recommended') => {
    if (SC.page?.products?.length) {
      setProductIds(
        key,
        SC.page.products.map((p) => p.id),
      )
    }
  }

  const go = useCallback(
    (extraKeys?: string[]) => {
      devLog('go', { extraKeys })
      ensureQueue('go')

      if (queue.length === 0) {
        console.error('[EmarsysTracking][go] ScarabQueue is empty')
        throw new Error('ScarabQueue is empty')
      }

      const hasDefaultEmarsysTracking = queue.some((item) => item[0] === 'cart')

      if (!hasDefaultEmarsysTracking) {
        console.error('[EmarsysTracking][go] Default emarsys tracking is not defined')
        throw new Error('Default emarsys tracking is not defined')
      }

      const hasExtraKeys =
        extraKeys && extraKeys.every((key) => queue.some((item) => item[0] === key))

      if (extraKeys && !hasExtraKeys) {
        console.error('[EmarsysTracking][go] Extra keys are not defined', extraKeys)
        throw new Error('Extra keys are not defined')
      }

      typedWindow.ScarabQueue.push(['go'])
      queue.length = 0
    },
    [queue, ensureQueue, devLog],
  )

  return {
    isTrackingReady,
    setDefaultSettings,
    setCategoryVisit,
    setHomepageVisit,
    setCustomPageVisit,
    setProductVisit,
    setPurchase,
    setShoppingCartVisit,
    go,
  }
}
